import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { PortalI18nNamespace, PortalLocale } from './constants';
import { setPortalLocale } from './I18nProvider';
import { getStoredPortalLocale } from './portalLocaleEvents';
import { getUserMenuLabels } from './userMenuLabels';
import { shellEngineLoadingText, shellLoadingText } from './shellText';
import { getAgentNetworkShellLabels } from './serviceLabels/agentNetwork';
import { getAgentNetworkOverviewLabels } from './serviceLabels/agentNetworkOverview';
import { getAgentNetworkSharedLabels } from './serviceLabels/agentNetworkShared';
import { getAgentNetworkConsumerPageLabels } from './serviceLabels/agentNetworkConsumerPages';
import { getAgentNetworkAdminPageLabels } from './serviceLabels/agentNetworkAdminPages';
import { getAssetGovernanceShellLabels, getAssetGovernanceMappingLabels } from './serviceLabels/assetGovernance';
import { getAiCyberopsSidebarTabs, getAiCyberopsCategoryOrder, getAiCyberopsWorkbenchLabels } from './serviceLabels/aiCyberops';
import { getIamTagsShellLabels } from './serviceLabels/iamTags';
import { getAiopsShellLabels } from './serviceLabels/aiops';
import { getCloudopsDashboardLabels } from './serviceLabels/cloudopsAwsDashboard';
import { getIamAssetsOverviewLabels, getIamAssetsNetworkNavLabels } from './serviceLabels/iamAssetsOverview';
import { getAgentSystemShellLabels } from './serviceLabels/agentSystemShell';
import { getAgentSystemOverviewLabels } from './serviceLabels/agentSystemOverview';
import { getAgentSystemAdminPageLabels } from './serviceLabels/agentSystemAdminPages';
import { useLocaleTick } from './localeTick';

export { useLocaleTick } from './localeTick';

export function usePortalTranslation(ns?: PortalI18nNamespace | PortalI18nNamespace[]) {
  return useTranslation(ns);
}

export function useLocale() {
  const { i18n } = useTranslation();
  const fromI18n = (i18n.language?.startsWith('en') ? 'en' : 'ko') as PortalLocale;
  const tick = useLocaleTick();
  const locale = useMemo(() => {
    if (i18n.isInitialized) return fromI18n;
    return getStoredPortalLocale();
  }, [fromI18n, tick, i18n.isInitialized]);

  const setLocale = useCallback(async (next: PortalLocale) => {
    await setPortalLocale(next);
  }, []);

  return { locale, setLocale };
}

/** Portal SPA — react-i18next + layout.json */
export function useUserMenuLabels() {
  const { t, i18n } = usePortalTranslation('layout');
  return useMemo(
    () => ({
      extend: t('userMenu.extend'),
      reLogin: t('userMenu.reLogin'),
      logout: t('userMenu.logout'),
      logoutConfirm: t('userMenu.logoutConfirm'),
      myAccount: t('userMenu.myAccount'),
      expired: t('userMenu.expired'),
      orgPrefix: t('userMenu.orgPrefix'),
      positionPrefix: t('userMenu.positionPrefix'),
    }),
    [t, i18n.language],
  );
}

/** Micro-FE — localStorage locale, no I18nProvider */
export function useUserMenuLabelsFromStorage() {
  const tick = useLocaleTick();
  return useMemo(() => getUserMenuLabels(), [tick]);
}

/** Micro-FE — reacts to portal locale change */
export function useShellLoadingText(): string {
  const tick = useLocaleTick();
  return useMemo(() => shellLoadingText(), [tick]);
}

/** Micro-FE — UserMenu + loading copy for UnifiedLayout shell */
export function useServiceShellI18n() {
  const userMenuLabels = useUserMenuLabelsFromStorage();
  const loadingText = useShellLoadingText();
  return { userMenuLabels, loadingText };
}

/** Micro-FE — optional engine overlay (e.g. asset-topology) */
export function useShellEngineLoadingText(): string {
  const tick = useLocaleTick();
  return useMemo(() => shellEngineLoadingText(), [tick]);
}

export function useAgentNetworkShellLabels() {
  const tick = useLocaleTick();
  return useMemo(() => getAgentNetworkShellLabels(), [tick]);
}

export function useAgentNetworkOverviewLabels() {
  const tick = useLocaleTick();
  return useMemo(() => getAgentNetworkOverviewLabels(), [tick]);
}

export function useAgentNetworkSharedLabels() {
  const tick = useLocaleTick();
  return useMemo(() => getAgentNetworkSharedLabels(), [tick]);
}

export function useAgentNetworkConsumerPageLabels() {
  const tick = useLocaleTick();
  return useMemo(() => getAgentNetworkConsumerPageLabels(), [tick]);
}

export function useAgentNetworkAdminPageLabels() {
  const tick = useLocaleTick();
  return useMemo(() => getAgentNetworkAdminPageLabels(), [tick]);
}

export function useAssetGovernanceShellLabels() {
  const tick = useLocaleTick();
  return useMemo(() => getAssetGovernanceShellLabels(), [tick]);
}

export function useAssetGovernanceMappingLabels() {
  const tick = useLocaleTick();
  return useMemo(() => getAssetGovernanceMappingLabels(), [tick]);
}

export function useAiCyberopsShellLabels() {
  const tick = useLocaleTick();
  return useMemo(
    () => ({
      tabs: getAiCyberopsSidebarTabs(),
      categoryOrder: getAiCyberopsCategoryOrder(),
      workbench: getAiCyberopsWorkbenchLabels(),
    }),
    [tick],
  );
}

export function useIamTagsShellLabels() {
  const tick = useLocaleTick();
  return useMemo(() => getIamTagsShellLabels(), [tick]);
}

export function useAiopsShellLabels() {
  const tick = useLocaleTick();
  return useMemo(() => getAiopsShellLabels(), [tick]);
}

export function useCloudopsDashboardLabels() {
  const tick = useLocaleTick();
  return useMemo(() => getCloudopsDashboardLabels(), [tick]);
}

export function useIamAssetsOverviewLabels() {
  const tick = useLocaleTick();
  return useMemo(() => getIamAssetsOverviewLabels(), [tick]);
}

export function useIamAssetsNetworkNavLabels() {
  const tick = useLocaleTick();
  return useMemo(() => getIamAssetsNetworkNavLabels(), [tick]);
}

export function useAgentSystemShellLabels() {
  const tick = useLocaleTick();
  return useMemo(() => getAgentSystemShellLabels(), [tick]);
}

export function useAgentSystemOverviewLabels() {
  const tick = useLocaleTick();
  return useMemo(() => getAgentSystemOverviewLabels(), [tick]);
}

export function useAgentSystemAdminPageLabels() {
  const tick = useLocaleTick();
  return useMemo(() => getAgentSystemAdminPageLabels(), [tick]);
}
